import { PageNavigation } from "@components/cms/page-navigation/PageNavigation";
import { PageSeo } from "@components/seo/PageSeo";
import type { IModuleText, IPageTwoColumns } from "@contentful-api/types/contentful";
import { ModulesRenderer } from "@lib/contentful/modules-renderer/ModulesRenderer";
import { hasWindow } from "@lib/utils/hasWindow";
import { H } from "@ui/components/content/heading/A11yHeading";
import { Box } from "@ui/components/layout/box/Box";
import type { FlexProps } from "@ui/components/layout/flex/Flex";
import { Flex } from "@ui/components/layout/flex/Flex";
import { Grid, GridItem } from "@ui/components/layout/grid/Grid";
import { deserialize } from "@ungap/structured-clone";
import { Children, useEffect, type FC, type ReactNode } from "react";

export type TwoColumnsPageProps = {
    leftColumnOrder?: FlexProps["order"];
    rightColumnOrder?: FlexProps["order"];
    navigationTitle?: string;
    serializedPage: any[][];
    children?: ReactNode;
    pathName: string;
    showTitle?: boolean;
    showNavigation?: boolean;
    isPrivateDataInTitle?: boolean;
};

declare const window: Window & { AudioEye: any };

export const TwoColumnsPage: FC<TwoColumnsPageProps> = ({
    rightColumnOrder,
    navigationTitle,
    leftColumnOrder,
    isPrivateDataInTitle,
    serializedPage,
    pathName,
    children,
    showTitle = true,
    showNavigation = true,
}) => {
    const page: IPageTwoColumns = deserialize(serializedPage);
    const seoMetadata = page?.fields?.seoMetadata;
    const leftColumnModules = page?.fields?.leftColumnModules;
    const rightColumnModules = page?.fields?.rightColumnModules;
    const pageNavigationLinks = page?.fields?.pageNavigation;
    const childrenArray = Children?.toArray(children);
    const isAccessibilityStatementPage = page?.fields?.isAccessibilityStatementPage;

    const isPrivacyPolicyLinksModule = rightColumnModules?.find(
        (module: IModuleText) => module.fields.key === "privacyPolicyLinks"
    );

    useEffect(() => {
        if (isAccessibilityStatementPage && hasWindow) {
            window.AudioEye?.ClientElementApi.init();
        }
    }, []);

    return (
        <>
            {seoMetadata && <PageSeo seoModule={seoMetadata} pathName={pathName} />}
            {showNavigation && pageNavigationLinks && (
                <PageNavigation pageNavigationLinks={pageNavigationLinks} title={navigationTitle} />
            )}
            <Box w="full" pt={4} pb={[8, 12, 16]} px={[3, 4, 6]} bg="gray.50" m="0 !important">
                {showTitle && (
                    <H
                        data-privacy={`${isPrivateDataInTitle}`}
                        size="h2"
                        fontSize={["mobileH2", "desktopH2"]}
                        mb={[6, 6, 12]}
                    >
                        {page?.fields?.title}
                    </H>
                )}
                <Grid templateColumns={["1fr", "1fr", "1fr", "repeat(12,1fr)"]} gap={0.2}>
                    {/* This is our way of split the two columns and make the nav links first, rich text in the midle
                    and rest of the carts below. We are relying in keys and leftColumns property.
                    */}
                    {isPrivacyPolicyLinksModule && (
                        <Flex
                            display={{ base: "flex", xl: "none" }}
                            flexDirection="column"
                            gap={0.5}
                            order={leftColumnOrder || 0}
                            id="card-generic"
                        >
                            <ModulesRenderer
                                modules={[isPrivacyPolicyLinksModule]}
                                cardSize="sm"
                                wrapInsideCard
                                isLeftColumn
                            />
                        </Flex>
                    )}
                    <GridItem
                        colSpan={8}
                        flexDirection="column"
                        gap={0.5}
                        order={leftColumnOrder || 0}
                    >
                        {childrenArray[0]}
                        <ModulesRenderer
                            modules={leftColumnModules}
                            cardSize="sm"
                            wrapInsideCard
                            isLeftColumn
                        />
                    </GridItem>
                    <GridItem
                        colSpan={4}
                        flexDirection="column"
                        gap={0.2}
                        order={rightColumnOrder || 1}
                    >
                        {childrenArray[1]}
                        <ModulesRenderer
                            modules={rightColumnModules}
                            wrapInsideCard
                            isRightColumn
                        />
                    </GridItem>
                </Grid>
            </Box>
        </>
    );
};
